/**
 * PM Gallery -- lightbox del custom element <pm-gallery>. Un único
 * <dialog> por instancia (nunca uno por card), construido dinámicamente
 * en connectedCallback() e idempotente frente a rerenders del Shopify
 * Customizer (mismo patrón ya probado en pm-multibanner.js: guardas de
 * conexión + limpieza completa en disconnectedCallback()).
 *
 * MOTOR DE CARRUSEL: no se toca. Flickity/carousel-slider.js (Athens,
 * sin modificar) siguen siendo el único motor de movimiento -- este
 * archivo nunca llama a Flickity para mover nada.
 *
 * TAP vs. DRAG (_bindTriggers): en vez de depender del evento propio
 * 'staticClick' de Flickity (que en la práctica exigía que el dedo se
 * mantuviera casi perfectamente inmóvil -- el umbral interno de
 * Flickity está pensado para mouse, no para el movimiento natural de
 * un dedo real, y por eso hacían falta varios intentos para abrir en
 * móvil), esta versión usa Pointer Events propios (pointerdown /
 * pointerup / pointercancel) con un umbral de distancia pequeño y fijo
 * -- no un timer -- para decidir si un gesto fue un tap real o un
 * drag/swipe. Es la MISMA decisión para Grid, Scroller nativo y
 * Carrusel Flickity -- ya no hay dos rutas de apertura distintas.
 * Nunca se llama a preventDefault()/stopPropagation() ni se captura el
 * puntero, así que el drag nativo de Flickity y el scroll táctil
 * siguen funcionando exactamente igual que antes.
 *
 * CARGA PROGRESIVA (_openLightbox): el modal abre de inmediato con la
 * miniatura que la card ya tiene renderizada (mismo <img>, sin
 * descargar nada nuevo) y precarga la versión grande (1400px) en
 * paralelo con un Image() nativo -- si termina bien y el modal sigue
 * mostrando esa misma apertura, reemplaza el src; si falla, la
 * miniatura se queda puesta. El modal nunca se abre vacío ni queda
 * vacío.
 *
 * ZOOM HÍBRIDO: escritorio (hover:hover + pointer:fine) usa hover real
 * sobre .pm-gallery-lightbox-media -- escala fija ~1.5x con
 * transform-origin siguiendo al cursor, vía _bindZoomDesktop. Táctil
 * usa botones [-] 1.0x [+] con 4 niveles fijos (1/1.5/2/2.5) más pan
 * por arrastre cuando zoom>1, vía _bindZoomControls/_bindZoomPan. Los
 * dos caminos son mutuamente excluyentes por dispositivo y solo tocan
 * variables CSS distintas del mismo transform (ver comentario grande
 * en assets/pm-gallery.css sobre .pm-gallery-lightbox-img) -- nunca se
 * escala ni se mueve .pm-gallery-lightbox-media/-inner, ambos quedan
 * exactamente como los calculó _setLightboxMediaSize. La versión de
 * 2000px (más nítida que el 1400px de apertura) solo se pide la
 * primera vez que hay intención real de zoom (primer hover real en
 * escritorio, primer tap en '+' en móvil) -- nunca al abrir el modal.
 */
(() => {
  'use strict';

  if (customElements.get('pm-gallery')) return;

  // 1.0 / 1.5 / 2.0 / 2.5 -- niveles fijos del zoom táctil (botones).
  // El hover de escritorio usa su propio factor fijo, independiente de
  // esta lista (ver DESKTOP_ZOOM en _bindZoomDesktop).
  var ZOOM_LEVELS = [1, 1.5, 2, 2.5];

  // Contador compartido a nivel de módulo -- permite que varias
  // instancias de PM Gallery convivan en la misma página sin pisarse el
  // bloqueo de scroll del body: solo se bloquea al abrir el PRIMER
  // lightbox (de cualquier instancia) y solo se restaura cuando se
  // cierra el ÚLTIMO que quedaba abierto.
  var openLightboxCount = 0;
  var savedHtmlOverflow = '';

  function lockScroll() {
    if (openLightboxCount === 0) {
      savedHtmlOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    openLightboxCount += 1;
  }

  function unlockScroll() {
    openLightboxCount = Math.max(0, openLightboxCount - 1);
    if (openLightboxCount === 0) {
      document.documentElement.style.overflow = savedHtmlOverflow;
    }
  }

  class PmGallery extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;

      this.triggers = Array.prototype.slice.call(
        this.querySelectorAll('[data-pm-gallery-trigger]')
      );
      if (!this.triggers.length) return;

      this._connected = true;
      this._isOpen = false;
      this._activeTrigger = null;
      this._activePointer = null;

      this._buildDialog();
      this._bindTriggers();
      this._bindDialogEvents();
      this._bindZoomDesktop();
      this._bindZoomControls();
      this._bindZoomPan();
      this._resetZoomState();
      this._bindPrevArrowFix();
      this._bindAutoplayEndDelay();
    }

    disconnectedCallback() {
      this._connected = false;
      this._activePointer = null;

      if (this._autoplayEndRAF) {
        window.cancelAnimationFrame(this._autoplayEndRAF);
        this._autoplayEndRAF = null;
      }
      if (this._autoplayEndTimeout) {
        clearTimeout(this._autoplayEndTimeout);
        this._autoplayEndTimeout = null;
      }

      if (this._prevArrowRAF) {
        window.cancelAnimationFrame(this._prevArrowRAF);
        this._prevArrowRAF = null;
      }
      if (this._onPrevArrowClick) this.removeEventListener('click', this._onPrevArrowClick, true);

      if (this._onPointerDown) this.removeEventListener('pointerdown', this._onPointerDown);
      if (this._onPointerUp) this.removeEventListener('pointerup', this._onPointerUp);
      if (this._onPointerCancel) this.removeEventListener('pointercancel', this._onPointerCancel);

      if (this._media) {
        if (this._onMediaPointerEnter) this._media.removeEventListener('pointerenter', this._onMediaPointerEnter);
        if (this._onMediaPointerMove) this._media.removeEventListener('pointermove', this._onMediaPointerMove);
        if (this._onMediaPointerLeave) this._media.removeEventListener('pointerleave', this._onMediaPointerLeave);
        if (this._onMediaPanStart) this._media.removeEventListener('pointerdown', this._onMediaPanStart);
        if (this._onMediaPanMove) this._media.removeEventListener('pointermove', this._onMediaPanMove);
        if (this._onMediaPanEnd) {
          this._media.removeEventListener('pointerup', this._onMediaPanEnd);
          this._media.removeEventListener('pointercancel', this._onMediaPanEnd);
        }
      }
      if (this._zoomInBtn && this._onZoomInClick) {
        this._zoomInBtn.removeEventListener('click', this._onZoomInClick);
      }
      if (this._zoomOutBtn && this._onZoomOutClick) {
        this._zoomOutBtn.removeEventListener('click', this._onZoomOutClick);
      }
      if (this._pendingHighestRes) {
        this._pendingHighestRes.onload = null;
        this._pendingHighestRes.onerror = null;
        this._pendingHighestRes = null;
      }

      if (this._enterFrame1) {
        window.cancelAnimationFrame(this._enterFrame1);
        this._enterFrame1 = null;
      }
      if (this._enterFrame2) {
        window.cancelAnimationFrame(this._enterFrame2);
        this._enterFrame2 = null;
      }
      if (this._pendingHighRes) {
        this._pendingHighRes.onload = null;
        this._pendingHighRes.onerror = null;
        this._pendingHighRes = null;
      }

      if (this.triggers && this._onTriggerClick) {
        this.triggers.forEach((trigger) => {
          trigger.removeEventListener('click', this._onTriggerClick);
        });
      }

      if (this._dialog) {
        // close() dispara 'close' de forma SÍNCRONA -- para cuando esta
        // línea retorna, this._onDialogClose ya corrió (unlockScroll()
        // incluido), así que un rerender del Customizer nunca puede
        // dejar el body bloqueado ni un lightbox huérfano.
        if (this._dialog.open) this._dialog.close();
        if (this._onDialogClick) this._dialog.removeEventListener('click', this._onDialogClick);
        if (this._onDialogClose) this._dialog.removeEventListener('close', this._onDialogClose);
        if (this._closeBtn && this._onCloseClick) {
          this._closeBtn.removeEventListener('click', this._onCloseClick);
        }
        this._dialog.remove();
        this._dialog = null;
      }
    }

    _buildDialog() {
      var dialog = document.createElement('dialog');
      dialog.className = 'pm-gallery-lightbox';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-label', 'Imagen ampliada');

      // Tarjeta visual real del modal -- el <dialog> en sí sigue siendo
      // solo la superficie de centrado a pantalla completa (ver
      // assets/pm-gallery.css).
      var inner = document.createElement('div');
      inner.className = 'pm-gallery-lightbox-inner';

      // Fila propia para el botón cerrar, arriba de la imagen -- ya no
      // se posiciona absolute sobre la fotografía.
      var toolbar = document.createElement('div');
      toolbar.className = 'pm-gallery-lightbox-toolbar';

      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'pm-gallery-lightbox-close';
      closeBtn.setAttribute('aria-label', 'Cerrar');
      closeBtn.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
        '</svg>';

      // Contenedor de la imagen -- _setLightboxMediaSize le asigna
      // width/height en píxeles exactos antes de showModal(); ver esa
      // función para el cálculo completo.
      var media = document.createElement('div');
      media.className = 'pm-gallery-lightbox-media';

      var img = document.createElement('img');
      img.className = 'pm-gallery-lightbox-img';
      img.alt = '';

      // Fila propia de zoom táctil, DEBAJO de la imagen -- oculta por
      // CSS en escritorio con hover real (ver assets/pm-gallery.css).
      var zoomControls = document.createElement('div');
      zoomControls.className = 'pm-gallery-lightbox-zoom-controls';

      var zoomOutBtn = document.createElement('button');
      zoomOutBtn.type = 'button';
      zoomOutBtn.className = 'pm-gallery-lightbox-zoom-btn pm-gallery-lightbox-zoom-out';
      zoomOutBtn.setAttribute('aria-label', 'Reducir zoom');
      zoomOutBtn.textContent = '−';

      var zoomLevelEl = document.createElement('span');
      zoomLevelEl.className = 'pm-gallery-lightbox-zoom-level';

      var zoomInBtn = document.createElement('button');
      zoomInBtn.type = 'button';
      zoomInBtn.className = 'pm-gallery-lightbox-zoom-btn pm-gallery-lightbox-zoom-in';
      zoomInBtn.setAttribute('aria-label', 'Aumentar zoom');
      zoomInBtn.textContent = '+';

      zoomControls.appendChild(zoomOutBtn);
      zoomControls.appendChild(zoomLevelEl);
      zoomControls.appendChild(zoomInBtn);

      toolbar.appendChild(closeBtn);
      media.appendChild(img);
      inner.appendChild(toolbar);
      inner.appendChild(media);
      inner.appendChild(zoomControls);
      dialog.appendChild(inner);
      this.appendChild(dialog);

      this._dialog = dialog;
      this._inner = inner;
      this._toolbar = toolbar;
      this._media = media;
      this._img = img;
      this._closeBtn = closeBtn;
      this._zoomControls = zoomControls;
      this._zoomOutBtn = zoomOutBtn;
      this._zoomInBtn = zoomInBtn;
      this._zoomLevelEl = zoomLevelEl;
    }

    // Calcula, ANTES de abrir, el tamaño final en píxeles de
    // .pm-gallery-lightbox-media a partir de las dimensiones reales de
    // la imagen (data-pm-gallery-width/-height del trigger) y el
    // presupuesto de espacio disponible (max-width/max-height/padding
    // de .pm-gallery-lightbox-inner + la altura fija de
    // .pm-gallery-lightbox-toolbar y de .pm-gallery-lightbox-zoom-controls
    // -- esta última SOLO cuando su display resuelto no es "none" (en
    // escritorio con hover real no ocupa espacio, así que no se resta
    // nada ahí y el cálculo queda idéntico a antes de agregar el
    // zoom), todo leído vía getComputedStyle -- valores en rem/vw/vh se
    // resuelven aunque el <dialog> todavía esté cerrado/display:none,
    // porque no dependen de layout real, solo de la fuente/viewport
    // actuales). Preserva siempre la relación de aspecto real de la
    // imagen -- una imagen portrait termina más angosta que alta, una
    // landscape más ancha que alta -- y nunca amplía por encima del
    // tamaño natural de la imagen (Math.min(..., 1)), para no verse
    // borrosa. Este tamaño queda fijo mientras el modal está abierto:
    // nada más en todo el archivo vuelve a tocar el width/height de
    // .pm-gallery-lightbox-media -- el zoom transforma únicamente
    // .pm-gallery-lightbox-img (ver _bindZoomDesktop/_bindZoomPan).
    _setLightboxMediaSize(naturalWidth, naturalHeight) {
      var innerStyle = window.getComputedStyle(this._inner);
      var maxW = parseFloat(innerStyle.maxWidth) || window.innerWidth;
      var maxH = parseFloat(innerStyle.maxHeight) || window.innerHeight;
      var padH = (parseFloat(innerStyle.paddingLeft) || 0) + (parseFloat(innerStyle.paddingRight) || 0);
      var padV = (parseFloat(innerStyle.paddingTop) || 0) + (parseFloat(innerStyle.paddingBottom) || 0);
      var gap = parseFloat(innerStyle.rowGap) || 0;
      var toolbarH = parseFloat(window.getComputedStyle(this._toolbar).height) || 0;

      var zoomControlsStyle = window.getComputedStyle(this._zoomControls);
      var zoomControlsVisible = zoomControlsStyle.display !== 'none';
      var zoomControlsH = zoomControlsVisible ? parseFloat(zoomControlsStyle.height) || 0 : 0;
      var gapCount = zoomControlsVisible ? 2 : 1;

      var budgetW = Math.max(1, maxW - padH);
      var budgetH = Math.max(1, maxH - padV - gap * gapCount - toolbarH - zoomControlsH);

      var mediaW = budgetW;
      var mediaH = budgetH;
      if (naturalWidth > 0 && naturalHeight > 0) {
        var scale = Math.min(budgetW / naturalWidth, budgetH / naturalHeight, 1);
        mediaW = Math.round(naturalWidth * scale);
        mediaH = Math.round(naturalHeight * scale);
      }

      this._media.style.width = mediaW + 'px';
      this._media.style.height = mediaH + 'px';
    }

    // Estado de zoom/pan a "cero" -- se llama tanto al ABRIR una foto
    // nueva (ver _openLightbox: "cada fotografía nueva debe comenzar
    // zoom=1.0, pan=0,0, origen=centro") como al CERRAR el diálogo (ver
    // _onDialogClose: nunca debe quedar estado de una foto anterior
    // aplicado a la siguiente). Reutilizada en ambos puntos a
    // propósito -- es la misma limpieza exacta en los dos casos.
    _resetZoomState() {
      this._desktopZoomActive = false;
      this._zoomLevelIndex = 0;
      this._panX = 0;
      this._panY = 0;
      this._maxPanX = 0;
      this._maxPanY = 0;
      this._panPointerId = null;
      this._highestResRequested = false;
      this._appliedHighestRes = false;
      if (this._pendingHighestRes) {
        this._pendingHighestRes.onload = null;
        this._pendingHighestRes.onerror = null;
        this._pendingHighestRes = null;
      }
      if (this._media) this._media.classList.remove('pm-gallery-zoomed');
      if (this._img) {
        this._img.style.setProperty('--pm-gallery-zoom-scale', '1');
        this._img.style.setProperty('--pm-gallery-zoom-origin-x', '50%');
        this._img.style.setProperty('--pm-gallery-zoom-origin-y', '50%');
        this._img.style.setProperty('--pm-gallery-zoom-pan-x', '0px');
        this._img.style.setProperty('--pm-gallery-zoom-pan-y', '0px');
      }
      this._updateZoomControlsUI();
    }

    // ZOOM ESCRITORIO -- solo dispositivos con hover real y puntero
    // fino (comprobado en el momento del evento, no una sola vez al
    // conectar, por si el mismo dispositivo cambia de modo de entrada).
    // pointerType se filtra a 'mouse' explícitamente: en un dispositivo
    // táctil/híbrido esto nunca debe activar el zoom por hover, ese
    // camino es exclusivamente de los botones +/-.
    _bindZoomDesktop() {
      var DESKTOP_ZOOM = 1.5;
      var media = this._media;

      var hoverCapable = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this._onMediaPointerEnter = (event) => {
        if (event.pointerType !== 'mouse' || !hoverCapable()) return;
        this._desktopZoomActive = true;
        media.classList.add('pm-gallery-zoomed');
        this._img.style.setProperty('--pm-gallery-zoom-scale', String(DESKTOP_ZOOM));
        this._updateZoomOrigin(event);
        this._requestHighestRes();
      };

      this._onMediaPointerMove = (event) => {
        if (!this._desktopZoomActive || event.pointerType !== 'mouse') return;
        this._updateZoomOrigin(event);
      };

      this._onMediaPointerLeave = (event) => {
        if (event.pointerType !== 'mouse' || !this._desktopZoomActive) return;
        this._desktopZoomActive = false;
        media.classList.remove('pm-gallery-zoomed');
        this._img.style.setProperty('--pm-gallery-zoom-scale', '1');
        this._img.style.setProperty('--pm-gallery-zoom-origin-x', '50%');
        this._img.style.setProperty('--pm-gallery-zoom-origin-y', '50%');
      };

      media.addEventListener('pointerenter', this._onMediaPointerEnter);
      media.addEventListener('pointermove', this._onMediaPointerMove);
      media.addEventListener('pointerleave', this._onMediaPointerLeave);
    }

    // Posición relativa del cursor dentro de .pm-gallery-lightbox-media
    // (0%-100% en cada eje, con clamp) alimentada directamente a
    // transform-origin vía variables CSS -- cursor a la izquierda
    // prioriza la zona izquierda de la foto, arriba prioriza la zona
    // superior, etc. Nunca toca panX/panY (esas son del camino táctil).
    _updateZoomOrigin(event) {
      var rect = this._media.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var x = ((event.clientX - rect.left) / rect.width) * 100;
      var y = ((event.clientY - rect.top) / rect.height) * 100;
      x = Math.max(0, Math.min(100, x));
      y = Math.max(0, Math.min(100, y));
      this._img.style.setProperty('--pm-gallery-zoom-origin-x', x + '%');
      this._img.style.setProperty('--pm-gallery-zoom-origin-y', y + '%');
    }

    // ZOOM TÁCTIL -- botones [-] nivel [+], 4 niveles fijos. Ocultos
    // por CSS en escritorio con hover real (ver assets/pm-gallery.css),
    // por eso alcanzarlos ahí no es posible ni por click ni por
    // teclado (display:none los retira del árbol de accesibilidad).
    _bindZoomControls() {
      this._onZoomInClick = () => {
        this._requestHighestRes();
        this._setZoomLevel(this._zoomLevelIndex + 1);
      };
      this._onZoomOutClick = () => {
        this._setZoomLevel(this._zoomLevelIndex - 1);
      };
      this._zoomInBtn.addEventListener('click', this._onZoomInClick);
      this._zoomOutBtn.addEventListener('click', this._onZoomOutClick);
    }

    // Cambia de nivel de zoom (índice sobre ZOOM_LEVELS, con clamp a
    // los extremos -- así los propios botones deshabilitados nunca
    // llegan a disparar esto fuera de rango). Volver exactamente a
    // 1.0x resetea el pan a 0,0; cualquier otro nivel mantiene el pan
    // existente pero lo clampea a los nuevos límites (ver
    // _clampPanToLevel) -- nunca lo resetea de más.
    _setZoomLevel(index) {
      index = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, index));
      this._zoomLevelIndex = index;
      var level = ZOOM_LEVELS[index];

      if (level === 1) {
        this._panX = 0;
        this._panY = 0;
        this._maxPanX = 0;
        this._maxPanY = 0;
      } else {
        this._clampPanToLevel(level);
      }

      this._img.style.setProperty('--pm-gallery-zoom-scale', String(level));
      this._applyPanTransformVars();
      this._updateZoomControlsUI();
    }

    // scaledWidth/Height = tamaño real de .pm-gallery-lightbox-media
    // (la "ventana fija", nunca modificada por el zoom) multiplicado
    // por el nivel actual -- el exceso que ese escalado genera respecto
    // del tamaño real, repartido a cada lado, es el desplazamiento
    // máximo permitido en cada eje. clientWidth/Height (no el estilo
    // inline) para leer el tamaño real ya calculado por
    // _setLightboxMediaSize.
    _clampPanToLevel(level) {
      var mediaW = this._media.clientWidth;
      var mediaH = this._media.clientHeight;
      var maxPanX = Math.max(0, (mediaW * level - mediaW) / 2);
      var maxPanY = Math.max(0, (mediaH * level - mediaH) / 2);
      this._maxPanX = maxPanX;
      this._maxPanY = maxPanY;
      this._panX = Math.max(-maxPanX, Math.min(maxPanX, this._panX));
      this._panY = Math.max(-maxPanY, Math.min(maxPanY, this._panY));
    }

    _applyPanTransformVars() {
      this._img.style.setProperty('--pm-gallery-zoom-pan-x', this._panX + 'px');
      this._img.style.setProperty('--pm-gallery-zoom-pan-y', this._panY + 'px');
    }

    _updateZoomControlsUI() {
      var level = ZOOM_LEVELS[this._zoomLevelIndex];
      if (this._zoomLevelEl) this._zoomLevelEl.textContent = level.toFixed(1) + 'x';
      if (this._zoomOutBtn) this._zoomOutBtn.disabled = this._zoomLevelIndex === 0;
      if (this._zoomInBtn) this._zoomInBtn.disabled = this._zoomLevelIndex === ZOOM_LEVELS.length - 1;
    }

    // PAN TÁCTIL -- solo tiene efecto real cuando el nivel actual es
    // >1 (a 1.0x _setZoomLevel ya deja panX/panY en 0 y aquí se corta
    // de entrada). Independiente del tap-vs-drag de las thumbnails: se
    // escucha sobre .pm-gallery-lightbox-media, dentro del lightbox ya
    // abierto, sin ninguna relación con Flickity ni con el carrusel.
    // Nunca preventDefault()/stopPropagation() -- touch-action:none en
    // CSS (ver assets/pm-gallery.css) ya evita que el navegador
    // interprete el arrastre como gesto nativo.
    _bindZoomPan() {
      this._onMediaPanStart = (event) => {
        var level = ZOOM_LEVELS[this._zoomLevelIndex];
        if (level <= 1 || this._panPointerId !== null) return;
        this._panPointerId = event.pointerId;
        this._panStartX = event.clientX;
        this._panStartY = event.clientY;
        this._panStartPanX = this._panX;
        this._panStartPanY = this._panY;
      };

      this._onMediaPanMove = (event) => {
        if (this._panPointerId === null || event.pointerId !== this._panPointerId) return;
        var level = ZOOM_LEVELS[this._zoomLevelIndex];
        this._panX = this._panStartPanX + (event.clientX - this._panStartX);
        this._panY = this._panStartPanY + (event.clientY - this._panStartY);
        this._clampPanToLevel(level);
        this._applyPanTransformVars();
      };

      this._onMediaPanEnd = (event) => {
        if (this._panPointerId === null || event.pointerId !== this._panPointerId) return;
        this._panPointerId = null;
      };

      this._media.addEventListener('pointerdown', this._onMediaPanStart);
      this._media.addEventListener('pointermove', this._onMediaPanMove);
      this._media.addEventListener('pointerup', this._onMediaPanEnd);
      this._media.addEventListener('pointercancel', this._onMediaPanEnd);
    }

    // Deriva la URL de 2000px a partir de la MISMA URL de 1400px que ya
    // usa data-pm-gallery-full -- Shopify CDN acepta sobrescribir el
    // parámetro de consulta "width" directamente, así que esto no
    // necesita ningún dato nuevo desde Liquid (sections/pm-gallery.liquid
    // queda sin tocar).
    _buildHighestResSrc() {
      var trigger = this._activeTrigger;
      if (!trigger) return '';
      var fullSrc = trigger.getAttribute('data-pm-gallery-full');
      if (!fullSrc) return '';
      try {
        var url = new URL(fullSrc, window.location.href);
        url.searchParams.set('width', '2000');
        return url.toString();
      } catch (e) {
        return '';
      }
    }

    // Se llama desde la PRIMERA entrada real de hover en escritorio y
    // desde el PRIMER tap en "+" en móvil -- cualquiera de las dos
    // marca "ya hubo intención real de zoom". _highestResRequested
    // asegura que esto ocurra una sola vez por apertura, sin importar
    // cuántas veces se repita el gesto que lo dispara. Mientras carga
    // sigue mostrando lo que ya había (preview o 1400px); si falla, se
    // queda con lo que ya había. _appliedHighestRes evita que un
    // 1400px que termine de cargar DESPUÉS de esto haga retroceder la
    // imagen ya mejorada a 2000px (ver highRes.onload en _openLightbox).
    _requestHighestRes() {
      if (this._highestResRequested) return;
      this._highestResRequested = true;

      var highestSrc = this._buildHighestResSrc();
      if (!highestSrc) return;

      var img2000 = new Image();
      this._pendingHighestRes = img2000;
      img2000.onload = () => {
        if (this._pendingHighestRes !== img2000) return;
        this._pendingHighestRes = null;
        if (this._dialog && this._dialog.open) {
          this._img.src = highestSrc;
          this._appliedHighestRes = true;
        }
      };
      img2000.onerror = () => {
        if (this._pendingHighestRes === img2000) this._pendingHighestRes = null;
      };
      img2000.src = highestSrc;
    }

    // Decisión ÚNICA de apertura por gesto, igual para Grid, Scroller
    // nativo y Carrusel Flickity -- delegado a nivel de <pm-gallery>
    // (un solo listener por evento en vez de uno por trigger) usando
    // pointerId para saber, al soltar, a qué trigger correspondía el
    // pointerdown original, incluso si Flickity captura el puntero
    // internamente para su propio drag (la captura redirige el TARGET
    // del evento, no las coordenadas ni el pointerId, así que el
    // cálculo de distancia sigue siendo correcto igual).
    //
    // Umbral fijo de 10px -- no un timer: un tap real de un dedo real
    // rara vez se mantiene perfectamente inmóvil (de ahí que depender
    // del umbral interno de Flickity, pensado para mouse, exigiera
    // varios intentos), pero un swipe/drag real para cambiar de imagen
    // recorre muchos más píxeles que eso.
    _bindTriggers() {
      var TAP_THRESHOLD = 10;

      this._onPointerDown = (event) => {
        if (!event.isPrimary) return;
        var trigger = event.target.closest && event.target.closest('[data-pm-gallery-trigger]');
        if (!trigger) return;
        this._activePointer = {
          id: event.pointerId,
          trigger: trigger,
          x: event.clientX,
          y: event.clientY,
        };
      };

      this._onPointerUp = (event) => {
        var pointer = this._activePointer;
        if (!pointer || event.pointerId !== pointer.id) return;
        this._activePointer = null;

        var dx = event.clientX - pointer.x;
        var dy = event.clientY - pointer.y;
        var distance = Math.sqrt(dx * dx + dy * dy);

        // Se marca SIEMPRE, tap o drag -- el click nativo que el
        // navegador dispara después de este mismo gesto (mouse o
        // touch) no debe volver a decidir nada por su cuenta.
        pointer.trigger._pmGallerySuppressClick = true;

        if (distance <= TAP_THRESHOLD) {
          this._openLightbox(pointer.trigger);
        }
      };

      this._onPointerCancel = (event) => {
        var pointer = this._activePointer;
        if (!pointer || event.pointerId !== pointer.id) return;
        this._activePointer = null;
      };

      this.addEventListener('pointerdown', this._onPointerDown);
      this.addEventListener('pointerup', this._onPointerUp);
      this.addEventListener('pointercancel', this._onPointerCancel);

      // Único camino restante: activación por teclado (Enter/Space
      // sobre el trigger enfocado sintetiza 'click' sin ningún
      // pointerdown/pointerup previo -- _pmGallerySuppressClick nunca
      // se marca en ese caso, así que este fallback sigue abriendo con
      // normalidad y sin romper la navegación por teclado.
      this._onTriggerClick = (event) => {
        var trigger = event.currentTarget;
        if (trigger._pmGallerySuppressClick) {
          trigger._pmGallerySuppressClick = false;
          return;
        }
        this._openLightbox(trigger);
      };
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this._onTriggerClick);
      });
    }

    _bindDialogEvents() {
      this._onCloseClick = () => this._dialog.close();
      this._closeBtn.addEventListener('click', this._onCloseClick);

      // Click en el propio <dialog> (nunca en la imagen ni en el botón
      // cerrar, que son hijos distintos) equivale a "click en el
      // backdrop" -- ver la nota completa en assets/pm-gallery.css.
      this._onDialogClick = (event) => {
        if (event.target === this._dialog) this._dialog.close();
      };
      this._dialog.addEventListener('click', this._onDialogClick);

      // Escape dispara 'cancel' de forma nativa en <dialog>; se deja
      // que el propio navegador lo resuelva (que a su vez dispara
      // 'close') en vez de agregar un keydown propio.
      //
      // 'close' es el ÚNICO punto real de limpieza -- se dispara sin
      // importar si el cierre vino del botón X, del backdrop o de
      // Escape, así que la restauración de scroll y de foco nunca
      // depende de cuál de los tres caminos se usó.
      this._onDialogClose = () => {
        this._isOpen = false;
        unlockScroll();
        if (this._pendingHighRes) {
          this._pendingHighRes.onload = null;
          this._pendingHighRes.onerror = null;
          this._pendingHighRes = null;
        }
        // Limpia zoom, pan, el pointer de pan activo y cualquier
        // precarga de 2000px pendiente -- ninguna foto siguiente debe
        // heredar el estado de zoom de esta.
        this._resetZoomState();
        this._img.removeAttribute('src');
        if (this._activeTrigger) {
          this._activeTrigger.focus();
          this._activeTrigger = null;
        }
      };
      this._dialog.addEventListener('close', this._onDialogClose);
    }

    // CORRECCIÓN LOCAL -- flecha izquierda del carrusel Flickity exige a
    // veces dos clics para retroceder un solo paso visual. Causa real
    // confirmada leyendo assets/flickity.pkgd.js (proto._containSlides,
    // "contain ----- "): con contain:true, cellAlign:'left', cada
    // slide.target se clampea con Math.min(slide.target, endBound); con
    // celdas angostas y groupCells:false (necesario en PM Gallery, ver
    // comentario #6 en sections/pm-gallery.liquid) varias slides finales
    // consecutivas terminan con el MISMO target clampeado exacto aunque
    // selectedIndex sea distinto. previous() (proto.previous ->
    // select(selectedIndex-1)) SÍ cambia selectedIndex en el primer
    // clic, pero como el target físico no cambió, Flickity no anima
    // ningún movimiento visible -- el segundo clic recién llega a una
    // slide con target realmente distinto. assets/carousel-slider.js
    // (Athens, no modificado) no tiene ningún error: solo llama
    // flickity.previous()/next(), un comportamiento legítimo bajo esta
    // configuración concreta de Flickity.
    //
    // Fix 100% local a PM Gallery, sin tocar Flickity ni
    // carousel-slider.js: un listener de click en FASE DE CAPTURA sobre
    // <pm-gallery> (this) -- no sobre el propio botón -- porque dos
    // listeners registrados en el MISMO nodo se ejecutan en orden de
    // registro sin importar la fase; solo un listener de captura en un
    // ANCESTRO tiene garantía de ejecutarse antes de que el evento
    // llegue al botón y dispare el listener nativo de
    // carousel-navigation (ver assets/carousel-slider.js). Al detectar
    // el clic real sobre .js-prev de ESTA instancia, se busca hacia
    // atrás la primera slide cuyo target sea físicamente distinto
    // (tolerancia EPSILON de 1px, subpíxel) y se llama flkty.select()
    // directamente sobre ese índice -- select() dispara el evento
    // nativo 'select' igual que previous(), así que
    // assets/carousel-slider.js sigue actualizando enable/disableButton
    // sin ninguna lógica duplicada aquí. Si no hay ninguna slide previa
    // con target distinto (candidate < 0), esta función no hace nada y
    // deja que el clic siga su curso normal hacia el previous() nativo
    // de Athens -- nunca se simula un segundo clic ni se usa setTimeout.
    _bindPrevArrowFix() {
      var nav = this.querySelector('carousel-navigation');
      var prevBtn = nav && nav.querySelector('.js-prev');
      var carouselSlider = this.querySelector('carousel-slider');
      if (!prevBtn || !carouselSlider) return;

      this._prevArrowBtn = prevBtn;
      this._prevArrowFrames = 0;

      var waitForFlickity = () => {
        if (!this._connected) return;

        if (carouselSlider.flickity && carouselSlider.flickity.slides) {
          this._onPrevArrowClick = (event) => {
            var trigger = event.target.closest && event.target.closest('.js-prev');
            if (trigger !== this._prevArrowBtn) return;

            var flkty = carouselSlider.flickity;
            if (!flkty || !flkty.slides || !flkty.slides.length) return;

            var currentSlide = flkty.slides[flkty.selectedIndex];
            if (!currentSlide) return;

            var EPSILON = 1;
            var candidate = flkty.selectedIndex - 1;
            while (candidate >= 0) {
              var slide = flkty.slides[candidate];
              if (slide && Math.abs(slide.target - currentSlide.target) > EPSILON) {
                break;
              }
              candidate -= 1;
            }

            if (candidate < 0) return;

            event.preventDefault();
            event.stopPropagation();
            flkty.select(candidate);
          };
          this.addEventListener('click', this._onPrevArrowClick, true);
          return;
        }

        this._prevArrowFrames += 1;
        if (this._prevArrowFrames < 180) {
          this._prevArrowRAF = window.requestAnimationFrame(waitForFlickity);
        }
      };

      waitForFlickity();
    }

    // CORRECCIÓN LOCAL -- tiempo muerto de 15-20s al final del autoplay.
    // Causa real confirmada leyendo assets/flickity.pkgd.js
    // (Player.prototype.tick, "player & autoPlay"): el autoplay nativo
    // de Flickity llama siempre this.parent.next(true) con el MISMO
    // intervalo fijo (options.autoPlay), tick tras tick, sin ningún
    // caso especial para el final del carrusel -- next(true) fuerza
    // isWrap=true en select(), que aplica utils.modulo(index,
    // slides.length) SIN importar wrapAround global, y así vuelve solo
    // al índice 0 al pasar del último. El problema es que, por el mismo
    // clamping de contain:true documentado en _bindPrevArrowFix
    // (proto._containSlides -> Math.min(slide.target, endBound)),
    // varias slides finales consecutivas comparten el mismo target
    // físico: cada tick de esas slides "gastadas" avanza selectedIndex
    // sin mover nada visualmente, pero SÍ consume el intervalo normal
    // completo (options.autoPlay) antes del siguiente tick. Con 3-4
    // slides finales clampeadas al mismo target y un intervalo normal
    // de ~5s, el resultado percibido son exactamente esos 15-20s
    // "congelado" antes de saltar a la primera imagen.
    //
    // Fix 100% local a PM Gallery: se calcula una sola vez el target
    // MÁXIMO real entre todas las slides y el índice donde esa slide
    // aparece por primera vez ("lastVisualIndex" -- la única posición
    // visual verdaderamente distinta al final, sin volver a introducir
    // el problema de índices duplicados). Al asentarse (evento nativo
    // 'settle', no 'select', para reaccionar solo cuando el movimiento
    // ya terminó) sobre esa posición mientras el Player nativo de
    // Flickity está realmente reproduciendo (player.state === 'playing'
    // -- nunca si el usuario ya pausó/interactuó), se pausa el player
    // nativo (pausePlayer(), API pública de Flickity, sin tocar su
    // código) y se arma un único setTimeout propio con la duración
    // configurable (section.settings.autoplay_end_delay, en segundos,
    // convertido a ms aquí) que llama flkty.select(0) y reanuda el
    // player nativo (unpausePlayer()) -- un único retorno, sin
    // duplicar navegación. El intervalo normal entre slides intermedias
    // nunca se toca: sigue siendo 100% el Player nativo de Flickity con
    // options.autoPlay tal cual carousel-slider.js (Athens) lo
    // configuró. Cancelación: los mismos eventos nativos que ya paran
    // el autoplay de Flickity ante interacción real (pointerDown,
    // uiChange -- ver Flickity._createPlayer) se reutilizan aquí para
    // limpiar el setTimeout propio, así nunca queda un timer huérfano
    // disparándose sobre un estado que el usuario ya cambió a mano.
    _bindAutoplayEndDelay() {
      var carouselSlider = this.querySelector('carousel-slider');
      if (!carouselSlider) return;

      var endDelayAttr = carouselSlider.getAttribute('data-end-delay');
      var endDelayMs = (parseInt(endDelayAttr, 10) || 3) * 1000;

      this._autoplayEndFrames = 0;

      var waitForFlickity = () => {
        if (!this._connected) return;

        var flkty = carouselSlider.flickity;
        if (flkty && flkty.slides && flkty.slides.length) {
          if (carouselSlider.getAttribute('data-autoplay') !== 'true') return;

          var EPSILON = 1;
          var maxTarget = flkty.slides[0].target;
          flkty.slides.forEach((slide) => {
            if (slide.target > maxTarget) maxTarget = slide.target;
          });
          var lastVisualIndex = flkty.slides.findIndex((slide) => {
            return Math.abs(slide.target - maxTarget) <= EPSILON;
          });
          if (lastVisualIndex < 0) return;

          var clearPendingReturn = () => {
            if (this._autoplayEndTimeout) {
              clearTimeout(this._autoplayEndTimeout);
              this._autoplayEndTimeout = null;
            }
          };

          flkty.on('pointerDown', clearPendingReturn);
          flkty.on('uiChange', clearPendingReturn);

          flkty.on('settle', () => {
            if (flkty.selectedIndex !== lastVisualIndex) return;
            if (!flkty.player || flkty.player.state !== 'playing') return;
            if (this._autoplayEndTimeout) return;

            flkty.pausePlayer();
            this._autoplayEndTimeout = setTimeout(() => {
              this._autoplayEndTimeout = null;
              flkty.select(0);
              flkty.unpausePlayer();
            }, endDelayMs);
          });
          return;
        }

        this._autoplayEndFrames += 1;
        if (this._autoplayEndFrames < 180) {
          this._autoplayEndRAF = window.requestAnimationFrame(waitForFlickity);
        }
      };

      waitForFlickity();
    }

    _openLightbox(trigger) {
      if (!trigger || !this._dialog || this._dialog.open) return;

      var fullSrc = trigger.getAttribute('data-pm-gallery-full');
      if (!fullSrc) return;

      if (typeof this._dialog.showModal !== 'function') return;

      var altText = trigger.getAttribute('data-pm-gallery-alt') || '';
      var width = trigger.getAttribute('data-pm-gallery-width');
      var height = trigger.getAttribute('data-pm-gallery-height');

      // Cancela cualquier precarga high-res todavía pendiente de una
      // apertura anterior antes de arrancar esta -- su resultado, si
      // llegara tarde, nunca debe pisar la imagen de ESTA apertura.
      if (this._pendingHighRes) {
        this._pendingHighRes.onload = null;
        this._pendingHighRes.onerror = null;
        this._pendingHighRes = null;
      }

      this._activeTrigger = trigger;

      // Cada fotografía nueva empieza en zoom=1.0, pan=0,0, origen
      // centrado -- nunca hereda el estado de zoom de la apertura
      // anterior (también se llama al cerrar, ver _onDialogClose; se
      // llama aquí también por si esta apertura sucede sin que la
      // anterior haya llegado a disparar 'close' todavía).
      this._resetZoomState();

      // Apertura instantánea: reutiliza la miniatura que la card ya
      // tiene cargada en pantalla (currentSrc refleja la variante real
      // que el navegador eligió del srcset; src es el respaldo si por
      // algún motivo currentSrc todavía no está resuelto) -- no
      // descarga nada nuevo, el modal se abre con algo visible de
      // inmediato en vez de quedar vacío esperando la versión grande.
      var thumbImg = trigger.querySelector('img.pm-gallery-card-img');
      var previewSrc = thumbImg ? thumbImg.currentSrc || thumbImg.src : '';

      this._img.src = previewSrc || fullSrc;
      this._img.alt = altText;
      if (width) this._img.setAttribute('width', width);
      else this._img.removeAttribute('width');
      if (height) this._img.setAttribute('height', height);
      else this._img.removeAttribute('height');

      // Geometría final calculada AHORA, antes de showModal() -- la
      // preview abre directamente con el tamaño definitivo, nunca con
      // uno provisional que después salte al llegar la versión grande.
      this._setLightboxMediaSize(parseFloat(width) || 0, parseFloat(height) || 0);

      this._dialog.setAttribute(
        'aria-label',
        altText ? 'Imagen ampliada: ' + altText : 'Imagen ampliada'
      );

      // Entrada sutil (opacity + scale, ver assets/pm-gallery.css): la
      // clase se agrega ANTES de showModal() y se retira dos frames
      // después -- un cambio de estilo aplicado y revertido en el mismo
      // tick nunca dispara una transición css, por eso hace falta ese
      // margen de un frame real pintado en el estado inicial.
      this._dialog.classList.add('pm-gallery-is-entering');
      this._dialog.showModal();
      this._isOpen = true;
      lockScroll();
      this._closeBtn.focus();

      this._enterFrame1 = window.requestAnimationFrame(() => {
        this._enterFrame2 = window.requestAnimationFrame(() => {
          if (this._dialog) this._dialog.classList.remove('pm-gallery-is-entering');
        });
      });

      // Alta resolución en paralelo -- si la miniatura ya usada como
      // preview es exactamente la misma URL que fullSrc (imagen chica,
      // sin variantes intermedias) no hay nada que precargar de nuevo.
      if (previewSrc && previewSrc === fullSrc) return;

      var highRes = new Image();
      this._pendingHighRes = highRes;
      highRes.onload = () => {
        if (this._pendingHighRes !== highRes) return;
        this._pendingHighRes = null;
        // Si mientras esto cargaba ya se aplicó la versión de 2000px
        // (intención real de zoom detectada antes de que este 1400px
        // terminara), no retroceder la nitidez ya conseguida.
        if (this._appliedHighestRes) return;
        // Reemplazo deliberadamente mínimo: SOLO this._img.src cambia.
        // .pm-gallery-lightbox-media ya tiene su width/height final en
        // píxeles desde _setLightboxMediaSize, antes de showModal() --
        // esta línea nunca toca esa geometría, por eso la imagen no
        // salta de tamaño ni de posición al llegar la versión grande.
        if (this._activeTrigger === trigger && this._dialog && this._dialog.open) {
          this._img.src = fullSrc;
        }
      };
      highRes.onerror = () => {
        // Falla silenciosa a propósito: la preview ya visible se queda
        // puesta, el modal nunca debe quedar vacío por esto.
        if (this._pendingHighRes === highRes) this._pendingHighRes = null;
      };
      highRes.src = fullSrc;
    }
  }

  customElements.define('pm-gallery', PmGallery);
})();
